import { configureStore } from '@reduxjs/toolkit'

import {
  FLUSH,
  PAUSE,
  PERSIST, persistStore, PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist'

import { AchievementsReducer } from './achievements'
import { AuthReducer } from './auth'
import { ConfigReducer } from './config'
import { ConinfoBarReducer } from './coninfo/bar'
import { ConinfoMenuReducer } from './coninfo/menu'
import { ConinfoMessagesReducer } from './coninfo/messages'
import { ConinfoPhotosReducer } from './coninfo/photos'
import { ConinfoScreensReducer } from './coninfo/screens'
import { ErrorReducer } from './error'
import { EventsReducer } from './events'
import { EventsMenuReducer } from './events/menu'
import { LanguageReducer } from './lang'
import { MapReducer } from './map'
import { MenuReducer } from './menu'
import { NavigationReducer } from './navigation'
import { NotificationsReducer } from './notifications'
import { ThemeReducer } from './theme'
import { VotingReducer } from './voting'
import { FursuitVotingReducer } from './voting/fursuit'
import { TodayMenuReducer } from './today'
import { YapsReducer } from './yaps'
import { SnacksReducer } from './snacks'
import { ParkingReducer } from './parking'
import { TearoomReducer } from './tearoom'

// TODO: Use https://redux-toolkit.js.org/rtk-query instead of axios and custom data hooks
const appVersionStorageKey = 'app-build-version'
const backendVersionStorageKey = 'app-backend-version'
const legacyVersionStorageKey = 'cache-version'
const reduxPersistPrefix = 'persist:'

// Bump APP_COMPANION_FRONTEND_VERSION in deployment pipeline to trigger a hard refresh on clients.
// npm scripts also mirror it to REACT_APP_COMPANION_FRONTEND_VERSION for CRA-compatible injection.
const appBuildVersion = process.env.REACT_APP_COMPANION_FRONTEND_VERSION ?? process.env.APP_COMPANION_FRONTEND_VERSION ?? '0.0.0'

const getStoredVersion = () => {
  return localStorage.getItem(appVersionStorageKey) ??
    localStorage.getItem(legacyVersionStorageKey)
}

export const getStoredBackendVersion = () => {
  return localStorage.getItem(backendVersionStorageKey)
}

export const setStoredBackendVersion = (version) => {
  if (!version) {
    return
  }
  localStorage.setItem(backendVersionStorageKey, version)
}

const clearPersistedReduxState = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(reduxPersistPrefix)) {
      localStorage.removeItem(key)
    }
  })
  localStorage.removeItem(legacyVersionStorageKey)
}

const clearBrowserCaches = async () => {
  if (!('caches' in window)) {
    return
  }

  const cacheNames = await caches.keys()
  await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
}

const unregisterAllServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) {
    return
  }

  const registrations = await navigator.serviceWorker.getRegistrations()
  await Promise.all(registrations.map((registration) => registration.unregister()))
}

export const triggerHardReloadApplication = async () => {
  clearPersistedReduxState()
  await Promise.allSettled([
    clearBrowserCaches(),
    unregisterAllServiceWorkers()
  ])

  const url = new URL(window.location.href)
  url.searchParams.set('__v', appBuildVersion)
  window.location.replace(url.toString())
}

export const store = configureStore({
  reducer: {
    achievements: AchievementsReducer,
    auth: AuthReducer,
    navigation: NavigationReducer,
    menu: MenuReducer,
    notifications: NotificationsReducer,
    voting: VotingReducer,
    schedule: EventsReducer,
    language: LanguageReducer,
    error: ErrorReducer,
    map: MapReducer,
    config: ConfigReducer,
    theme: ThemeReducer,
    coninfoMenu: ConinfoMenuReducer,
    coninfoMessages: ConinfoMessagesReducer,
    coninfoPhotos: ConinfoPhotosReducer,
    coninfoBar: ConinfoBarReducer,
    coninfoScreens: ConinfoScreensReducer,
    eventsMenu: EventsMenuReducer,
    yaps: YapsReducer,
    fursuitVoting: FursuitVotingReducer,
    todayMenu: TodayMenuReducer,
    snacks: SnacksReducer,
    parking: ParkingReducer,
    tearoom: TearoomReducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
})

const storedVersion = getStoredVersion()
if (storedVersion !== appBuildVersion) {
  // Set first to avoid accidental reload loops if the clear process is interrupted.
  localStorage.setItem(appVersionStorageKey, appBuildVersion)
  triggerHardReloadApplication()
}

export const State = store // Todo: find usages and remove
export const persistor = persistStore(store)
export default store
